import { ClientRouter } from "@engaging-tech/routing"
import { getConfig } from "@engaging-tech/ssr-config"
import * as Sentry from "@sentry/react"
import { PostHogProvider } from "posthog-js/react"
import React from "react"
import { hydrate } from "react-dom"
import { useSSR } from "react-i18next"
import { Provider as StoreProvider } from "react-redux"
import { PersistGate } from "redux-persist/integration/react"
import { ThemeProvider } from "styled-components"

import App from "./App"
import "./locales/i18n"
import initilializeRedux from "./store"
import { workLTheme } from "./theme"

Sentry.init({
  dsn: process.env.RAZZLE_SENTRY_DSN,
  release: process.env.RAZZLE_SENTRY_RELEASE,
  environment: process.env.RAZZLE_DEPLOY_TARGET,
  enabled: process.env.RAZZLE_DEPLOY_TARGET === "prod",
  tracesSampleRate: 0.1,
  ignoreErrors: ["No current user"],
  // Cookie/CookieReadJS is injected by the Clickback tracking script.
  beforeSend(event) {
    const isThirdPartyClickbackError =
      event.exception?.values?.some(exception =>
        exception.stacktrace?.frames?.some(
          frame =>
            frame.module?.includes("Cookie/CookieReadJS") ||
            frame.filename?.includes("Cookie/CookieReadJS") ||
            frame.abs_path?.includes("Cookie/CookieReadJS")
        )
      ) ?? false

    if (isThirdPartyClickbackError) {
      return null
    }
    // Freshchat's third-party widget currently throws JSON parsing errors
    // from widget.js during initialisation. Observed variants include:
    // "Unexpected EOF", "Unexpected identifier \"object\"" and
    // "\"[object Object]\" is not valid JSON". These originate in
    // Freshchat's code rather than our application, so suppress them
    // until Freshchat provides a fix.
    const isFreshchatJsonParseError =
      event.exception?.values?.some(exception => {
        const isJsonParseError =
          exception.type === "SyntaxError" &&
          (exception.value?.includes("JSON Parse error") || exception.value?.includes("is not valid JSON"))

        const isFreshchatFrame =
          exception.stacktrace?.frames?.some(
            frame =>
              frame.filename?.includes("wchat.freshchat.com/js/widget.js") ||
              frame.abs_path?.includes("wchat.freshchat.com/js/widget.js") ||
              frame.filename?.includes("wchat.eu.freshchat.com/js/widget.js") ||
              frame.abs_path?.includes("wchat.eu.freshchat.com/js/widget.js")
          ) ?? false

        return isJsonParseError && isFreshchatFrame
      }) ?? false

    if (isFreshchatJsonParseError) {
      return null
    }
    // CefSharp errors, automated/browser infrastructure noise
    const isCefSharpError =
      event.exception?.values?.some(
        exception =>
          exception.type === "UnhandledRejection" &&
          /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/.test(
            exception.value ?? ""
          )
      ) ?? false

    if (isCefSharpError) {
      return null
    }
    return event
  }
})

const { store, persistor } = initilializeRedux()

const BaseApp = () => {
  const config = getConfig()
  useSSR(window.initialI18nStore, window.initialLanguage)
  const options = {
    api_host: config.posthog.api_host,
    ui_host: config.posthog.ui_host
  }
  return (
    <PostHogProvider apiKey={config.posthog.key} options={options}>
      <StoreProvider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ClientRouter>
            <ThemeProvider theme={workLTheme}>
              <App />
            </ThemeProvider>
          </ClientRouter>
        </PersistGate>
      </StoreProvider>
    </PostHogProvider>
  )
}
hydrate(
  <Sentry.ErrorBoundary>
    <BaseApp />
  </Sentry.ErrorBoundary>,
  document.getElementById("root")
)

if (module.hot) {
  module.hot.accept()
}
